import { Box } from "@mui/material";
import { useMemo, useRef } from "react";
import ReactFlow, {
  Background,
  ReactFlowProvider,
  Node,
  Edge,
  Controls,
  MiniMap,
  ConnectionMode,
  Viewport,
  useOnViewportChange,
} from "reactflow";
import "reactflow/dist/style.css";

import { PeopleIndex, Person } from "../types/person";
import { useAncestorTree } from "../hooks/useAncestorTree";
import PersonNode from "./PersonNode";
import CoupleNode from "./CoupleNode";
import useCoupleExpansion from "../hooks/useCoupleExpansion";

// Callback function types for library consumers
export interface AncestorTreeCallbacks {
  /** Called when a person node is clicked */
  onPersonClick?: (person: Person) => void;
  /** Called when a couple node is clicked */
  onCoupleClick?: (partner1: Person, partner2: Person) => void;
  /** Called when the tree is panned */
  onTreePan?: (x: number, y: number) => void;
  /** Called when the tree is zoomed */
  onTreeZoom?: (zoom: number) => void;
  /** Called when the tree viewport changes (pan or zoom) */
  onViewportChange?: (x: number, y: number, zoom: number) => void;
  /** Called when a couple is expanded/collapsed */
  onCoupleExpansion?: (
    partner1: Person | undefined,
    partner2: Person | undefined,
    isExpanded: boolean
  ) => void;
}

// UI control props for showing/hiding features
export interface AncestorTreeUIControls {
  /** Show/hide the zoom and fit controls */
  showControls?: boolean;
  /** Show/hide the mini map */
  showMiniMap?: boolean;
  /** Show/hide the background grid */
  showBackground?: boolean;
  /** Enable/disable panning */
  enablePan?: boolean;
  /** Enable/disable zooming */
  enableZoom?: boolean;
  /** Enable/disable fit view on mount */
  enableFitView?: boolean;
  /** Custom background color */
  backgroundColor?: string;
  /** Node height (affects vertical spacing calculation) */
  nodeHeight?: number;
  /** Vertical gaps between nodes for each generation [gen0, gen1, gen2, gen3, gen4...] */
  verticalGaps?: number[];
  /** Default vertical gap when generation not specified in verticalGaps */
  defaultVerticalGap?: number;
  /** Custom formatter for person subtitle text */
  formatPersonSubtitle?: (person: Person) => string;
  /** Width of couple nodes */
  coupleNodeWidth?: number;
  /** Width of person nodes */
  personNodeWidth?: number;
}

interface Props {
  people: PeopleIndex;
  rootId: string;
  /** Callback functions for various tree interactions */
  callbacks?: AncestorTreeCallbacks;
  /** UI control options */
  uiControls?: AncestorTreeUIControls;
}

// Layout configuration
const LAYOUT_CONFIG = {
  nodeHeight: 120,
  // X positions for each generation
  xPositions: [0, 225, 600, 1000, 1400],
  defaultXStep: 200,
  // Vertical gaps between nodes in each generation
  verticalGaps: [0, 325, 100, 325, 100],
  defaultGap: 50,
  // Edge styling
  edgeStyle: { stroke: "#999", strokeWidth: 3 },
} as const;

type PersonToCouple = Record<string, string>;
type CoupleToPartners = Record<string, [Person, Person]>;

interface NodeData {
  partner1?: Person;
  partner2?: Person;
  person?: Person;
  gen: number;
  isEdge?: boolean;
  onExpand?: () => void;
  onPersonClick?: (person: Person) => void;
  onCoupleClick?: (partner1: Person, partner2: Person) => void;
  formatPersonSubtitle?: (person: Person) => string;
  coupleNodeWidth?: number;
  personNodeWidth?: number;
}

export default function AncestorTree({
  people,
  rootId,
  callbacks = {},
  uiControls = {},
}: Props) {
  const { generations } = useAncestorTree(people, rootId);
  const { expandedId, token, expand } = useCoupleExpansion();

  // Default UI control values
  const {
    showControls = true,
    showMiniMap = false,
    showBackground = true,
    enablePan = true,
    enableZoom = true,
    enableFitView = true,
    backgroundColor = "#fafafa",
    nodeHeight = 120,
    verticalGaps = [0, 325, 100, 325, 100],
    defaultVerticalGap = 50,
    formatPersonSubtitle,
    coupleNodeWidth = 320,
    personNodeWidth = 160,
  } = uiControls;

  // Create layout config from props
  const layoutConfig = useMemo(() => {
    // Base configuration
    const baseXPositions = [0, 225, 600, 1000, 1400];
    const baseCoupleWidth = 320;

    // Calculate width difference from baseline
    const widthDiff = coupleNodeWidth - baseCoupleWidth;

    // Apply cumulative adjustment to X-positions
    const adjustedXPositions = baseXPositions.map(
      (x, index) => x + index * widthDiff
    );

    return {
      nodeHeight,
      // Dynamically calculated X positions
      xPositions: adjustedXPositions,
      defaultXStep: 200 + widthDiff,
      // Vertical gaps between nodes in each generation
      verticalGaps,
      defaultGap: defaultVerticalGap,
      // Node widths
      coupleNodeWidth,
      personNodeWidth,
      // Edge styling
      edgeStyle: { stroke: "#999", strokeWidth: 3 },
    };
  }, [
    nodeHeight,
    coupleNodeWidth,
    personNodeWidth,
    verticalGaps,
    defaultVerticalGap,
  ]);

  const flowKey = `${token}-${
    expandedId ?? "root"
  }-${showBackground}-${showControls}-${showMiniMap}`;

  // Store couple mappings for callback access
  const coupleToPartnersRef = useRef<CoupleToPartners>({});

  // Handle couple expansion with callback
  const handleCoupleExpansion = (coupleId: string) => {
    const wasExpanded = expandedId === coupleId;
    expand(coupleId);

    // Get the couple's Person objects for the callback
    const couple = coupleToPartnersRef.current[coupleId];
    const [partner1, partner2] = couple || [undefined, undefined];

    callbacks.onCoupleExpansion?.(
      wasExpanded ? undefined : partner1,
      wasExpanded ? undefined : partner2,
      !wasExpanded
    );
  };

  // Viewport change handler component - must be inside ReactFlow
  const ViewportChangeHandler = () => {
    useOnViewportChange({
      onChange: ({ x, y, zoom }: Viewport) => {
        callbacks.onViewportChange?.(x, y, zoom);
        callbacks.onTreePan?.(x, y);
        callbacks.onTreeZoom?.(zoom);
      },
    });
    return null;
  };

  const { nodes, edges } = useMemo(() => {
    const nodes: Node<NodeData>[] = [];
    const edges: Edge[] = [];
    const personToCouple: PersonToCouple = {};
    const coupleToPartners: CoupleToPartners = {};

    // Build base generations (0-2)
    generations.forEach((generation, generationIndex) => {
      const { nodes: generationNodes, mappings } = buildGenerationNodes(
        generation,
        generationIndex,
        handleCoupleExpansion,
        layoutConfig,
        callbacks.onPersonClick,
        formatPersonSubtitle
      );

      nodes.push(...generationNodes);
      Object.assign(personToCouple, mappings.personToCouple);
      Object.assign(coupleToPartners, mappings.coupleToPartners);
    });

    // Update the ref with the couple mappings for callback access
    coupleToPartnersRef.current = { ...coupleToPartners };

    // Build edges between base generations
    const baseEdges = buildBaseGenerationEdges(nodes, personToCouple);
    edges.push(...baseEdges);

    // Build expanded generations if needed
    if (expandedId && coupleToPartners[expandedId]) {
      const expandedResult = buildExpandedGenerations(
        people,
        expandedId,
        coupleToPartners,
        nodes,
        flowKey,
        layoutConfig,
        callbacks.onPersonClick,
        formatPersonSubtitle
      );

      nodes.push(...expandedResult.nodes);
      edges.push(...expandedResult.edges);
      Object.assign(personToCouple, expandedResult.mappings.personToCouple);
      Object.assign(coupleToPartners, expandedResult.mappings.coupleToPartners);

      // Update the ref with expanded mappings too
      coupleToPartnersRef.current = { ...coupleToPartners };
    }

    // Filter edges to only include those with valid source/target nodes
    const validNodeIds = new Set(nodes.map((node) => node.id));
    const validEdges = edges.filter(
      (edge) => validNodeIds.has(edge.source) && validNodeIds.has(edge.target)
    );

    // Filter edges based on expansion state
    const finalEdges = filterEdgesByExpansionState(
      validEdges,
      expandedId,
      nodes
    );

    return { nodes, edges: finalEdges };
  }, [
    generations,
    expandedId,
    token,
    people,
    callbacks.onPersonClick,
    flowKey,
    uiControls.formatPersonSubtitle,
    layoutConfig,
  ]);

  return (
    <ReactFlowProvider>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          backgroundColor: showBackground ? "transparent" : backgroundColor,
        }}
      >
        <ReactFlow
          key={flowKey}
          nodes={nodes}
          edges={edges}
          nodeTypes={{
            person: PersonNode,
            couple: CoupleNode,
          }}
          panOnScroll={enablePan}
          zoomOnScroll={enableZoom}
          fitView={enableFitView}
          zoomOnDoubleClick={false}
          connectionMode={ConnectionMode.Loose}
          defaultEdgeOptions={{
            type: "step",
            animated: false,
            style: { strokeWidth: 3 },
          }}
        >
          <ViewportChangeHandler />
          {showBackground && <Background gap={16} color="#eee" />}
          {showControls && <Controls />}
          {showMiniMap && (
            <MiniMap
              nodeColor={(node) => {
                switch (node.type) {
                  case "person":
                    return "#4fc3f7";
                  case "couple":
                    return "#81c784";
                  default:
                    return "#eee";
                }
              }}
              nodeStrokeWidth={3}
              zoomable
              pannable
            />
          )}
        </ReactFlow>
      </Box>
    </ReactFlowProvider>
  );
}

// Helper functions

function getGenerationLayout(
  generationIndex: number,
  coupleCount: number,
  layoutConfig: typeof LAYOUT_CONFIG
) {
  const xPosition =
    layoutConfig.xPositions[generationIndex] ??
    layoutConfig.xPositions[layoutConfig.xPositions.length - 1] +
      (generationIndex - layoutConfig.xPositions.length + 1) *
        layoutConfig.defaultXStep;

  const gap =
    layoutConfig.verticalGaps[generationIndex] ?? layoutConfig.defaultGap;
  const totalHeight =
    coupleCount * layoutConfig.nodeHeight + (coupleCount - 1) * gap;
  const startY = -totalHeight / 2;

  return { xPosition, gap, startY };
}

function buildGenerationNodes(
  generation: Person[],
  generationIndex: number,
  expand: (id: string) => void,
  layoutConfig: any,
  onPersonClick?: (person: Person) => void,
  formatPersonSubtitle?: (person: Person) => string
) {
  const nodes: Node<NodeData>[] = [];
  const personToCouple: PersonToCouple = {};
  const coupleToPartners: CoupleToPartners = {};

  const coupleCount = Math.ceil(generation.length / 2);
  const { xPosition, gap, startY } = getGenerationLayout(
    generationIndex,
    coupleCount,
    layoutConfig
  );

  for (let i = 0; i < generation.length; i += 2) {
    const partner1 = generation[i];
    const partner2 = generation[i + 1];
    const coupleIndex = i / 2;
    const yPosition = startY + coupleIndex * (layoutConfig.nodeHeight + gap);

    if (partner2) {
      // Couple node
      const coupleId = `c-${partner1.id}-${partner2.id}`;
      nodes.push({
        id: coupleId,
        position: { x: xPosition, y: yPosition },
        type: "couple",
        data: {
          partner1,
          partner2,
          gen: generationIndex,
          onExpand: generationIndex === 2 ? () => expand(coupleId) : undefined,
          onPersonClick,
          formatPersonSubtitle,
          coupleNodeWidth: layoutConfig.coupleNodeWidth,
        },
      });

      personToCouple[partner1.id] = coupleId;
      personToCouple[partner2.id] = coupleId;
      coupleToPartners[coupleId] = [partner1, partner2];
    } else {
      // Single person node
      nodes.push({
        id: partner1.id,
        position: { x: xPosition, y: yPosition },
        type: "person",
        data: {
          person: partner1,
          gen: generationIndex,
          isEdge: !!partner1.parentIds?.length,
          onPersonClick,
          formatPersonSubtitle,
          personNodeWidth: layoutConfig.personNodeWidth,
        },
      });
    }
  }

  return {
    nodes,
    mappings: { personToCouple, coupleToPartners },
  };
}

function buildBaseGenerationEdges(
  nodes: Node<NodeData>[],
  personToCouple: PersonToCouple
): Edge[] {
  const edges: Edge[] = [];
  const edgeSet = new Set<string>();
  const processedCouples = new Set<string>();

  // Process each couple to create edges to their parents
  Object.values(personToCouple).forEach((coupleId) => {
    if (processedCouples.has(coupleId)) return;
    processedCouples.add(coupleId);

    const coupleNode = nodes.find((node) => node.id === coupleId);
    if (!coupleNode?.data.partner1 || !coupleNode?.data.partner2) return;

    const { partner1, partner2 } = coupleNode.data;

    // Create edge from dad handle to dad's parents
    const dadEdge = createParentEdge(
      coupleId,
      partner1,
      "dad",
      personToCouple,
      LAYOUT_CONFIG
    );
    if (dadEdge && !edgeSet.has(dadEdge.id)) {
      edgeSet.add(dadEdge.id);
      edges.push(dadEdge);
    }

    // Create edge from mom handle to mom's parents
    const momEdge = createParentEdge(
      coupleId,
      partner2,
      "mom",
      personToCouple,
      LAYOUT_CONFIG
    );
    if (momEdge && !edgeSet.has(momEdge.id)) {
      edgeSet.add(momEdge.id);
      edges.push(momEdge);
    }
  });

  return edges;
}

function createParentEdge(
  coupleId: string,
  person: Person,
  handleType: "dad" | "mom",
  personToCouple: PersonToCouple,
  layoutConfig: any
): Edge | null {
  if (!person.parentIds) return null;

  const [parent1, parent2] = person.parentIds;
  const targetCouple = parent1
    ? personToCouple[parent1]
    : parent2
    ? personToCouple[parent2]
    : undefined;

  if (!targetCouple) return null;

  return {
    id: `${coupleId}-${handleType}-${targetCouple}`,
    source: coupleId,
    sourceHandle: handleType,
    target: targetCouple,
    type: "step",
    style: layoutConfig.edgeStyle,
  };
}

function buildExpandedGenerations(
  people: PeopleIndex,
  expandedId: string,
  coupleToPartners: CoupleToPartners,
  existingNodes: Node<NodeData>[],
  flowKey: string,
  layoutConfig: any,
  onPersonClick?: (person: Person) => void,
  formatPersonSubtitle?: (person: Person) => string
) {
  const nodes: Node<NodeData>[] = [];
  const edges: Edge[] = [];
  const personToCouple: PersonToCouple = {};
  const coupleToPartners_new: CoupleToPartners = {};

  const [selectedDad, selectedMom] = coupleToPartners[expandedId];

  // Build generation 4 (parents of selected couple)
  const generation4 = buildGenerationFromParents(
    [selectedDad, selectedMom],
    people
  );
  // Build generation 5 (grandparents of selected couple)
  const generation5 = buildGenerationFromParents(generation4, people);

  const expandedGenerations = [generation4, generation5];
  const generation4CoupleIds: string[] = [];

  // Get the anchor position for centering expanded generations
  const selectedNode = existingNodes.find((node) => node.id === expandedId);
  const anchorY = selectedNode
    ? selectedNode.position.y + layoutConfig.nodeHeight / 2
    : 0;

  expandedGenerations.forEach((generation, index) => {
    const generationIndex = 3 + index; // 3 for gen4, 4 for gen5
    const coupleCount = Math.ceil(generation.length / 2);
    const gap =
      layoutConfig.verticalGaps[generationIndex] ?? layoutConfig.defaultGap;
    const totalHeight =
      coupleCount * layoutConfig.nodeHeight + (coupleCount - 1) * gap;
    const startY = anchorY - totalHeight / 2;
    const xPosition =
      layoutConfig.xPositions[generationIndex] ??
      layoutConfig.xPositions[layoutConfig.xPositions.length - 1];

    for (let i = 0; i < generation.length; i += 2) {
      const partner1 = generation[i];
      const partner2 = generation[i + 1];
      const coupleIndex = i / 2;
      const yPosition = startY + coupleIndex * (layoutConfig.nodeHeight + gap);

      if (partner2) {
        const coupleId = `c-${partner1.id}-${partner2.id}`;
        nodes.push({
          id: coupleId,
          position: { x: xPosition, y: yPosition },
          type: "couple",
          data: {
            partner1,
            partner2,
            gen: generationIndex,
            onPersonClick,
            formatPersonSubtitle,
            coupleNodeWidth: layoutConfig.coupleNodeWidth,
          },
        });

        personToCouple[partner1.id] = coupleId;
        personToCouple[partner2.id] = coupleId;
        coupleToPartners_new[coupleId] = [partner1, partner2];

        if (index === 0) {
          // Generation 4 - create arrow edge from selected couple
          generation4CoupleIds.push(coupleId);
          edges.push({
            id: `t${flowKey}-${expandedId}-arrow-${coupleId}`,
            source: expandedId,
            sourceHandle: "arrow",
            target: coupleId,
            type: "step",
            style: layoutConfig.edgeStyle,
          });
        }
      } else {
        nodes.push({
          id: partner1.id,
          position: { x: xPosition, y: yPosition },
          type: "person",
          data: {
            person: partner1,
            gen: generationIndex,
            isEdge: !!partner1.parentIds?.length,
            onPersonClick,
            formatPersonSubtitle,
            personNodeWidth: layoutConfig.personNodeWidth,
          },
        });
      }
    }
  });

  // Create edges from generation 4 to generation 5
  const generation4Edges = buildGeneration4ToGeneration5Edges(
    generation4CoupleIds,
    coupleToPartners_new,
    personToCouple,
    flowKey,
    layoutConfig
  );
  edges.push(...generation4Edges);

  return {
    nodes,
    edges,
    mappings: { personToCouple, coupleToPartners: coupleToPartners_new },
  };
}

function buildGenerationFromParents(
  parents: Person[],
  people: PeopleIndex
): Person[] {
  const result: Person[] = [];
  parents.forEach((parent) => {
    parent.parentIds?.forEach((parentId) => {
      if (parentId) {
        const person = people[parentId];
        if (person) result.push(person);
      }
    });
  });
  return result;
}

function buildGeneration4ToGeneration5Edges(
  generation4CoupleIds: string[],
  coupleToPartners: CoupleToPartners,
  personToCouple: PersonToCouple,
  flowKey: string,
  layoutConfig: any
): Edge[] {
  const edges: Edge[] = [];
  const edgeSet = new Set<string>();

  generation4CoupleIds.forEach((sourceId) => {
    const [partner1, partner2] = coupleToPartners[sourceId];

    [partner1, partner2].forEach((partner) => {
      partner.parentIds?.forEach((parentId) => {
        if (!parentId) return;

        const targetCouple = personToCouple[parentId];
        if (!targetCouple) return;

        const handleType = partner === partner1 ? "dad" : "mom";
        const edgeId = `t${flowKey}-${sourceId}-${handleType}-${targetCouple}`;

        if (!edgeSet.has(edgeId)) {
          edgeSet.add(edgeId);
          edges.push({
            id: edgeId,
            source: sourceId,
            sourceHandle: handleType,
            target: targetCouple,
            type: "step",
            style: layoutConfig.edgeStyle,
          });
        }
      });
    });
  });

  return edges;
}

function filterEdgesByExpansionState(
  edges: Edge[],
  expandedId: string | undefined,
  nodes: Node<NodeData>[]
): Edge[] {
  if (expandedId) {
    // When expanded, show all edges
    return edges;
  }

  // When collapsed, only show edges connecting base generations (0, 1, 2)
  return edges.filter((edge) => {
    if (edge.id.startsWith("t")) return false;

    const sourceNode = nodes.find((node) => node.id === edge.source);
    const targetNode = nodes.find((node) => node.id === edge.target);

    return (sourceNode?.data.gen ?? 0) <= 2 && (targetNode?.data.gen ?? 0) <= 2;
  });
}
